import type {
  KidDataTemplateModel,
  KidMarker,
  KidTemplateContentChangedModel,
  KidTemplateType,
} from '../../../../../api/kids/types';

export type PdfTag =
  | 'text'
  | 'sri_chart'
  | 'table'
  | 'costs_over_time_table'
  | 'composition_of_costs_table'
  | 'performance_scenarios_table'
  | 'page_break'
  | 'divider'
  | 'heading';

export type SetTableTag = Extract<
  PdfTag,
  | 'costs_over_time_table'
  | 'composition_of_costs_table'
  | 'performance_scenarios_table'
>;

/** Blocks that may only appear once per document. */
export const SINGLETON_BLOCK_TAGS = [
  'sri_chart',
  'costs_over_time_table',
  'composition_of_costs_table',
  'performance_scenarios_table',
] as const satisfies readonly PdfTag[];

export type SingletonBlockTag = (typeof SINGLETON_BLOCK_TAGS)[number];

export function isSingletonBlockTag(tag: string): tag is SingletonBlockTag {
  return (SINGLETON_BLOCK_TAGS as readonly string[]).includes(tag);
}

export type LayoutTag = Extract<
  PdfTag,
  'section_header' | 'page_break' | 'footer' | 'title'
>;

export type LayoutConfig = {
  fixed_width?: number[];
  margin?: number;
  margin_top?: number; // CSS: margin-top
  margin_bottom?: number;
  margin_left?: number;
  margin_right?: number;
};

export type FormatOptions = {
  fontFamily?: string; // CSS: font-family
  fontSize?: number; // CSS: font-size
  color?: string; // CSS: color
  backgroundColor?: string; // CSS: background-color
  textAlign?: 'left' | 'right' | 'center' | 'justify'; // CSS: text-align

  marginTop?: number; // CSS: margin-top
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  margin?: number;

  padding?: number; // one value for all sides (like CSS shorthand)
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;

  borderRadius?: number; // CSS: border-radius
  borderWidth?: number; // CSS: border-width
  borderColor?: string; // CSS: border-color

  bold?: boolean; // CSS: font-weight
  italic?: boolean; // CSS: font-style
  underline?: boolean; // CSS: text-decoration
  layout?: LayoutConfig;
};

export interface TableStyleConfig {
  fontName?: string;
  fontSize?: number;
  textColor?: string; // hex
  alignment?: 'LEFT' | 'CENTER' | 'RIGHT' | 'DECIMAL';
  valign?: 'TOP' | 'MIDDLE' | 'BOTTOM';

  leftPadding?: number;
  rightPadding?: number;
  topPadding?: number;
  bottomPadding?: number;

  background?: string; // entire table background
  rowBackgrounds?: string[];
  showGrid?: boolean;

  headerFontName?: string;
  headerFontSize?: number;
  headerTextColor?: string;
  headerBackground?: string;
  headerAlignment?: 'LEFT' | 'CENTER' | 'RIGHT' | 'DECIMAL';

  borderRadius?: number;
}

export type NestedPDFElement = {
  tag: string;
  content: string | string[][];
  format_options?: FormatOptions | TableStyleConfig;
};

export type PdfElement = {
  id?: string;
  fieldId: string;
  tag: string;
  content:
    | string
    | number
    | string[][]
    | number[][]
    | NestedPDFElement[];
  format_options?: FormatOptions | TableStyleConfig;
  title?: string;
  layout?: LayoutConfig;
  template_id?: string;
  template?: KidDataTemplateModel;
  existing_markers?: KidMarker[];
  rendered_content?: string;
};

export interface HeaderFooterConfig {
  enabled: boolean;
  allPages: boolean;
  version?: 'default' | 'centered' | 'two_tier';
}

export type KidDocumentForm = {
  client: string;
  fund_name: string;
  fund_name_full: string;
  share_class: string;
  date: string;
  elements: PdfElement[];
  /**
   * Template changes collected while editing (sent as `updated_kid_templates` on save).
   * Mirrors backend `KidTemplateContentChangedModel`.
   */
  updated_kid_templates?: KidTemplateContentChangedModel[];
};

export type KidComparisonData = {
  client: string;
  fund_name: string;
  fund_name_full: string;
  share_class: string;
  threshold: 'ch' | 'word' | 'para';
  comparison_version: any[];
  target_version: any[];
};

// ---- Mock Publish KID Documents ----
export type PublishKidDocumentInput = {
  shareClassId: string;
  fundId: string;
  fundName: string;
  isin: string;
  version: string;
};

export type PublishKidDocumentsBatchInput = {
  items: PublishKidDocumentInput[];
};

export type UpdatedKidTemplate = KidTemplateContentChangedModel;
